/**
 * ============================================
 * APP SIDEBAR - Menú de Navegación Principal
 * ============================================
 *
 * Este componente define el sidebar (menú lateral) del LIMS.
 * Contiene todos los módulos del sistema organizados por categorías.
 *
 * MEJORAS UX:
 * - Avatar con iniciales del usuario
 * - Dropdown menu para acciones de usuario
 * - Muestra rol en lugar de email (más útil)
 * - Navegación agrupada por categorías
 * - Sub-menús colapsables en Administración
 */

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts";
import { isGlobalAdmin } from "@/contexts/sucursal-context";
import {
  LayoutDashboard,
  CalendarClock,
  Users,
  Stethoscope,
  ClipboardList,
  Receipt,
  TestTubes,
  FlaskConical,
  CheckCircle,
  FileText,
  Wallet,
  UserCog,
  Settings,
  Package,
  LogOut,
  ChevronsUpDown,
  User,
  Building2,
  Monitor,
  Layers,
  Handshake,
  Search,
  FileKey2,
  DollarSign,
  BookOpen,
  ChevronRight,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Definir tipo para los items del menú
interface MenuItem {
  translationKey: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

interface SubMenuGroup {
  translationKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: MenuItem[];
}

// Grupo 1: Operaciones principales
const operacionesItems: MenuItem[] = [
  { translationKey: "nav.dashboard", url: "/", icon: LayoutDashboard },
  { translationKey: "nav.turnos", url: "/turnos", icon: CalendarClock },
  { translationKey: "nav.pacientes", url: "/pacientes", icon: Users },
  { translationKey: "nav.medicosRemitentes", url: "/medicos-remitentes", icon: Stethoscope },
  { translationKey: "nav.ordenes", url: "/ordenes", icon: ClipboardList },
  { translationKey: "nav.facturacion", url: "/facturacion", icon: Receipt },
];

// Grupo 2: Proceso de laboratorio
const laboratorioItems: MenuItem[] = [
  { translationKey: "nav.muestras", url: "/muestras", icon: TestTubes },
  { translationKey: "nav.procesamiento", url: "/procesamiento", icon: FlaskConical },
  { translationKey: "nav.validacion", url: "/validacion", icon: CheckCircle },
  { translationKey: "nav.informes", url: "/informes", icon: FileText },
];

// Grupo 3: Administración — items directos (sin sub-menú)
const administracionDirectItems: MenuItem[] = [
  { translationKey: "nav.caja", url: "/caja", icon: Wallet },
  { translationKey: "nav.inventario", url: "/inventario", icon: Package },
  { translationKey: "nav.configuracion", url: "/configuracion", icon: Settings },
];

// Grupo 3: Administración — sub-menús colapsables
const administracionSubMenus: SubMenuGroup[] = [
  {
    translationKey: "nav.groups.catalog",
    icon: BookOpen,
    items: [
      { translationKey: "nav.examenes", url: "/examenes", icon: FlaskConical },
      { translationKey: "nav.perfiles", url: "/perfiles", icon: Layers },
      { translationKey: "nav.tiposContenedor", url: "/tipos-contenedor", icon: TestTubes },
    ],
  },
  {
    translationKey: "nav.groups.fiscal",
    icon: FileKey2,
    items: [
      { translationKey: "nav.facturacionCAI", url: "/facturacion/cai", icon: FileKey2 },
      {
        translationKey: "nav.facturacionPrecios",
        url: "/facturacion/listas-precios",
        icon: DollarSign,
      },
    ],
  },
  {
    translationKey: "nav.groups.organization",
    icon: Building2,
    items: [
      { translationKey: "nav.clientes", url: "/clientes", icon: Handshake },
      { translationKey: "nav.sucursales", url: "/sucursales", icon: Building2, adminOnly: true },
      { translationKey: "nav.puntosAtencion", url: "/puntos-atencion", icon: Monitor },
      { translationKey: "nav.usuarios", url: "/usuarios", icon: UserCog },
    ],
  },
];

/**
 * Genera las iniciales del usuario para el avatar
 * Ejemplo: "Juan Molina" → "JM"
 */
function getInitials(nombre: string, apellido: string): string {
  const firstInitial = nombre?.charAt(0)?.toUpperCase() || "";
  const lastInitial = apellido?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}` || "??";
}

/**
 * Obtiene el nombre del rol del usuario
 */
function getRoleName(user: { role: { id: number; name: string } | null } | null): string {
  if (!user?.role) return "Sin rol";
  return user.role.name;
}

export function AppSidebar({ onSearchClick }: { onSearchClick?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();

  const isAdmin = isGlobalAdmin(user);

  const isActive = (url: string) => {
    if (url === "/") return location.pathname === "/";
    if (url === "/facturacion") return location.pathname === "/facturacion";
    return location.pathname.startsWith(url);
  };

  // Inicializar sub-menús abiertos si la ruta actual coincide con algún item
  const getInitialOpenState = (group: SubMenuGroup) =>
    group.items.some((item) => isActive(item.url));

  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      administracionSubMenus.map((group) => [group.translationKey, getInitialOpenState(group)])
    )
  );

  const toggleSubMenu = (key: string) =>
    setOpenSubMenus((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const renderMenuItems = (items: MenuItem[]) => (
    <SidebarMenu>
      {items
        .filter((item) => !item.adminOnly || isAdmin)
        .map((item) => {
          const title = t(item.translationKey);
          return (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton tooltip={title} isActive={isActive(item.url)} asChild>
                <Link to={item.url}>
                  <item.icon />
                  <span>{title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
    </SidebarMenu>
  );

  return (
    <Sidebar>
      {/* Header con logo */}
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <img
            src="/logo-transparent.png"
            alt="Molina Valdez"
            className="h-10 w-10 object-contain"
          />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Molina Valdez</span>
            <span className="text-xs text-muted-foreground">LIMS</span>
          </div>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSearchClick} tooltip={t("busquedaAvanzada.title")}>
              <Search />
              <span>{t("busquedaAvanzada.title")}</span>
              <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                ⌘K
              </kbd>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Grupo: Operaciones */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.groups.operations")}</SidebarGroupLabel>
          <SidebarGroupContent>{renderMenuItems(operacionesItems)}</SidebarGroupContent>
        </SidebarGroup>

        {/* Grupo: Laboratorio */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.groups.laboratory")}</SidebarGroupLabel>
          <SidebarGroupContent>{renderMenuItems(laboratorioItems)}</SidebarGroupContent>
        </SidebarGroup>

        {/* Grupo: Administración */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.groups.administration")}</SidebarGroupLabel>
          <SidebarGroupContent>
            {/* Items directos */}
            {renderMenuItems(administracionDirectItems)}

            {/* Sub-menús colapsables */}
            <SidebarMenu>
              {administracionSubMenus.map((group) => {
                const visibleItems = group.items.filter((item) => !item.adminOnly || isAdmin);
                if (visibleItems.length === 0) return null;
                const isOpen = openSubMenus[group.translationKey] ?? false;
                const groupTitle = t(group.translationKey);
                return (
                  <Collapsible
                    key={group.translationKey}
                    open={isOpen}
                    onOpenChange={() => toggleSubMenu(group.translationKey)}
                    asChild
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={groupTitle}>
                          <group.icon />
                          <span>{groupTitle}</span>
                          <ChevronRight
                            className={`ml-auto h-4 w-4 transition-transform duration-200 ${
                              isOpen ? "rotate-90" : ""
                            }`}
                          />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {visibleItems.map((item) => {
                            const title = t(item.translationKey);
                            return (
                              <SidebarMenuSubItem key={item.url}>
                                <SidebarMenuSubButton isActive={isActive(item.url)} asChild>
                                  <Link to={item.url}>
                                    <item.icon />
                                    <span>{title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer con usuario y dropdown menu */}
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                      {user ? getInitials(user.nombre, user.apellido) : "??"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user ? `${user.nombre} ${user.apellido}` : "Usuario"}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {getRoleName(user)}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="top"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                        {user ? getInitials(user.nombre, user.apellido) : "??"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user ? `${user.nombre} ${user.apellido}` : "Usuario"}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate("/configuracion")}>
                  <User className="mr-2 h-4 w-4" />
                  {t("user.profile")}
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => navigate("/configuracion")}>
                  <Settings className="mr-2 h-4 w-4" />
                  {t("user.settings")}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("auth.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
